import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { cn, useDialogFocus } from "@hybrid-iptv/ui";
import { InstallButton } from "./InstallButton.js";
import { CatalogRebuildNotice } from "./CatalogRebuildNotice.js";
import { GuideUpdateNotice } from "./GuideUpdateNotice.js";
import { parseVoiceCommand, useVoiceSearch } from "../search/useVoiceSearch.js";
import { useNotifications } from "../library/notificationsStore.js";
import { addRecent, suggestionsFor, useRecentSearches } from "../search/recentSearches.js";
import { useProfiles } from "../settings/profileStore.js";
import { LauncherLinks } from "../launcher/LauncherLinks.js";
import { launcherAvailable } from "../launcher/launcherApi.js";
import { useLauncher } from "../launcher/launcherStore.js";

/**
 * Global search bar (Section 6 shell). Typing searches on submit; the mic
 * (Section 11 step 5) works from ANY page — the transcript is parsed and the
 * result lands on /search with the query (and an optional content-type
 * filter), so "play espn" from Live TV jumps straight to the espn results.
 * Chromium-only; other browsers render no mic.
 *
 * The bell (Section 12 notifications) opens the in-app alert center — alerts
 * are recorded by NotificationEngine whether or not the browser grants OS
 * notification permission, so a TV sees them here.
 */
/** A short pause after the last keystroke, so a fast typist does not navigate per letter. */
const LIVE_DELAY_MS = 250;

export function TopBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState("");
  // Launcher mode puts the box's links beside search (LauncherLinks); the search box narrows for them.
  // The flag alone, not useLauncherHome: the bar needs no launcher data of its own.
  const launcherOn = useLauncher((s) => s.enabled) && launcherAvailable();

  // Results as you type (owner, 2026-10-13: "it should bring up results as you type after 2
  // characters"). From two characters the Search page shows the results and follows the box;
  // the first jump is a new page (Back returns to where the viewer was), the rest rewrite it.
  // `live` tells the page not to take the keyboard's field for itself: typing carries on here.
  const onSearchPage = location.pathname === "/search";
  const liveTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(liveTimer.current), []);
  const typeQuery = (value: string) => {
    setQuery(value);
    window.clearTimeout(liveTimer.current);
    const q = value.trim();
    if (q.length < 2) return;
    liveTimer.current = window.setTimeout(() => {
      navigate(`/search?q=${encodeURIComponent(q)}`, { replace: onSearchPage, state: { live: true } });
    }, LIVE_DELAY_MS);
  };
  // Recent searches of the active profile, offered under the box while it has focus. Few on
  // purpose: on a small TV the keyboard covers the lower half, and the Search page lists all ten.
  const profileId = useProfiles((state) => state.activeProfileId);
  const recent = useRecentSearches(profileId);
  const suggestions = suggestionsFor(recent, query, 5);
  const [searchFocused, setSearchFocused] = useState(false);

  const voice = useVoiceSearch((text) => {
    const { query: voiceQuery, filter: voiceFilter } = parseVoiceCommand(text);
    if (!voiceQuery) return;
    setQuery(voiceQuery);
    addRecent(profileId, voiceQuery);
    const params = new URLSearchParams({ q: voiceQuery });
    if (voiceFilter) params.set("filter", voiceFilter);
    navigate(`/search?${params.toString()}`);
  });

  // --- In-app notification bell ---
  const inbox = useNotifications((s) => s.inbox);
  const markInboxRead = useNotifications((s) => s.markInboxRead);
  const dismissInbox = useNotifications((s) => s.dismissInbox);
  const clearInbox = useNotifications((s) => s.clearInbox);
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  // Like every other panel: focus goes in, a remote's Back closes it and focus
  // returns to the bell. It closed only on Escape or a tap outside, so on a TV
  // Back went to the page behind with the panel still open (2026-09-26).
  useDialogFocus({ open, onClose: () => setOpen(false), rootRef: panelRef });
  const wrapRef = useRef<HTMLDivElement>(null);

  const unread = inbox.reduce((n, a) => n + (a.read ? 0 : 1), 0);

  // Opening the inbox marks everything read (standard notification-center
  // behavior — the badge clears the moment you look).
  useEffect(() => {
    if (open) markInboxRead();
  }, [open, markInboxRead]);

  // Focus into the panel and close on Escape / outside clicks — TV-remote
  // operable (buttons only, no hover-only actions).
  useEffect(() => {
    if (!open) return;
    panelRef.current?.focus();
    function onPointerDown(event: PointerEvent) {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  return (
    <header className={cn("topbar", launcherOn && "topbar--launcher")}>
      <form
        className="topbar__search"
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          const q = query.trim();
          window.clearTimeout(liveTimer.current);
          if (q) addRecent(profileId, q);
          navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search", { replace: onSearchPage, state: { live: true, submitted: true } });
        }}
        // Focus anywhere in the box, its mic or a suggestion keeps the suggestions up; leaving
        // for another control puts them away.
        onFocus={() => setSearchFocused(true)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setSearchFocused(false);
        }}
      >
        <svg viewBox="0 0 24 24" aria-hidden>
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <input
          value={query}
          onChange={(event) => typeQuery(event.target.value)}
          placeholder={launcherOn ? "Search…" : "Search channels, movies, series, EPG…"}
          aria-label="Search"
        />
        {voice.supported && (
          <button
            type="button"
            className={cn("topbar-mic", voice.listening && "topbar-mic--listening", voice.error && "topbar-mic--error")}
            aria-pressed={voice.listening}
            aria-label={voice.listening ? "Stop voice search" : "Search by voice"}
            title={voice.listening ? "Stop listening" : voice.error ?? "Search by voice"}
            onClick={() => (voice.listening ? voice.stop() : voice.start())}
          >
            <svg viewBox="0 0 24 24" aria-hidden>
              <path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-2.08A7 7 0 0 0 19 12h-2z" />
            </svg>
          </button>
        )}
        {searchFocused && suggestions.length > 0 && (
          <div className="topbar__suggest" role="group" aria-label="Recent searches">
            {suggestions.map((term) => (
              <button
                key={term}
                type="button"
                className="topbar__suggest-item"
                onClick={() => {
                  setQuery(term);
                  addRecent(profileId, term);
                  setSearchFocused(false);
                  navigate(`/search?q=${encodeURIComponent(term)}`);
                }}
              >
                {term}
              </button>
            ))}
          </div>
        )}
      </form>
      {launcherOn && <LauncherLinks />}
      <div className="topbar__actions">
        <CatalogRebuildNotice compact />
        <GuideUpdateNotice />
        <div ref={wrapRef} className="notif-wrap">
          <button
            type="button"
            className={cn("topbar-bell", unread > 0 && "topbar-bell--has-unread")}
            aria-expanded={open}
            aria-label={unread > 0 ? `Alerts (${unread} unread)` : "Alerts"}
            title="Alerts"
            onClick={() => setOpen((o) => !o)}
          >
            <svg viewBox="0 0 24 24" aria-hidden>
              <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9z" />
              <path d="M13.7 21a2 2 0 0 1-3.4 0" />
            </svg>
            {unread > 0 && <span className="topbar-bell__badge">{unread > 9 ? "9+" : unread}</span>}
          </button>

          {open && (
            <div
              ref={panelRef}
              className="notif-panel"
              role="dialog"
              aria-label="Alerts"
              tabIndex={-1}
              onKeyDown={(event) => {
                if (event.key === "Escape") setOpen(false);
              }}
            >
              <div className="notif-panel__header">
                <span className="notif-panel__title">Alerts</span>
                {inbox.length > 0 && (
                  <button type="button" className="notif-panel__action" onClick={() => clearInbox()}>
                    Clear all
                  </button>
                )}
              </div>
              {inbox.length === 0 ? (
                <p className="notif-empty">
                  No alerts yet. Turn on Notifications in Settings to get
                  “starting soon” and airing alerts.
                </p>
              ) : (
                <ul className="notif-list">
                  {inbox.map((alert) => (
                    <li key={alert.id} className="notif-item">
                      <div className="notif-item__body">
                        <span className="notif-item__title">{alert.title}</span>
                        <span className="notif-item__meta">{alert.message} · {formatTime(alert.at)}</span>
                      </div>
                      <button
                        type="button"
                        className="notif-item__dismiss"
                        aria-label={`Dismiss alert for ${alert.title}`}
                        onClick={() => dismissInbox(alert.id)}
                      >
                        ✕
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <InstallButton />
      </div>
    </header>
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${m} ${ampm}`;
}
